import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProductCarousel from '@/components/feature/ProductCarousel';
import JsonLd from '@/components/feature/JsonLd';
import { SITE_URL, SITE_NAME, FAVICON_URL } from '@/lib/seo';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { productCategories } from '@/mocks/products';

const guides = [
  { id: 1, icon: 'ri-file-list-3-line', color: 'bg-primary-50 text-primary-700' },
  { id: 2, icon: 'ri-truck-line', color: 'bg-accent-50 text-accent-700' },
  { id: 3, icon: 'ri-camera-line', color: 'bg-secondary-50 text-secondary-700' },
  { id: 4, icon: 'ri-shield-check-line', color: 'bg-primary-50 text-primary-700' },
  { id: 5, icon: 'ri-building-4-line', color: 'bg-accent-50 text-accent-700' },
  { id: 6, icon: 'ri-lightbulb-line', color: 'bg-secondary-50 text-secondary-700' },
];

const popularSearches = [
  { key: 'popular_spreadsheet', href: '/allchinabuy-spreadsheet-guide' },
  { key: 'popular_review', href: 'https://kaspreadsheet.com', external: true },
  { key: 'popular_shipping', href: 'https://kaspreadsheet.com', external: true },
  { key: 'popular_coupon', href: 'https://kaspreadsheet.com', external: true },
  { key: 'popular_discount', href: 'https://kaspreadsheet.com', external: true },
  { key: 'popular_warehouse', href: 'https://kaspreadsheet.com', external: true },
  { key: 'popular_legit', href: 'https://kaspreadsheet.com', external: true },
  { key: 'popular_qc', href: 'https://kaspreadsheet.com', external: true },
];

const comparisons = [
  { key: 'vs_superbuy', href: 'https://kaspreadsheet.com' },
  { key: 'vs_cssbuy', href: 'https://kaspreadsheet.com' },
  { key: 'vs_sugargoo', href: 'https://kaspreadsheet.com' },
  { key: 'vs_kakobuy', href: 'https://kaspreadsheet.com' },
];

const faqs = [
  { q: 'faq_q1', a: 'faq_a1' },
  { q: 'faq_q2', a: 'faq_a2' },
  { q: 'faq_q3', a: 'faq_a3' },
  { q: 'faq_q4', a: 'faq_a4' },
  { q: 'faq_q5', a: 'faq_a5' },
  { q: 'faq_q6', a: 'faq_a6' },
  { q: 'faq_q7', a: 'faq_a7' },
  { q: 'faq_q8', a: 'faq_a8' },
];

const heroBgUrl = 'https://readdy.ai/api/search-image?query=Aerial%20view%20of%20luxury%20fashion%20boutique%20interior%20with%20warm%20amber%20lighting%20and%20%20curated%20clothing%20displays%20on%20wooden%20shelves%20minimal%20elegant%20editorial%20photography%20soft%20natural%20light%20warm%20tones%20high-end%20retail%20atmosphere%20no%20people&width=1920&height=1080&seq=hero-bg-01&orientation=landscape';

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: SITE_NAME,
      description:
        'AllChinaBuy spreadsheet resource hub with products, sellers, QC guides, shipping tips, and buying guides.',
      inLanguage: 'en',
    },
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: SITE_NAME,
      url: `${SITE_URL}/`,
      logo: {
        '@type': 'ImageObject',
        url: FAVICON_URL,
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is an AllChinaBuy spreadsheet?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'An AllChinaBuy spreadsheet is a curated collection of product links, seller information, and buying resources organized for easy reference. It helps shoppers find products efficiently.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I use an AllChinaBuy spreadsheet?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Simply browse the categories, click on product links to view details, and follow the buying guides to complete your purchase safely.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is AllChinaBuy legit?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'AllChinaBuy is a known shopping agent platform. Like any online service, it is important to understand the process, verify sellers, and follow safety guidelines.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is AllChinaBuy safe?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Safety depends on following best practices: verify sellers, use QC photos, understand shipping costs upfront, and read our safety guides.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much does AllChinaBuy shipping cost?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Shipping costs vary by destination, weight, and shipping method. Use our shipping guide to estimate costs before ordering.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does AllChinaBuy have a warehouse?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, AllChinaBuy operates warehouses where items are stored, inspected, and consolidated before international shipping.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does AllChinaBuy offer coupons or discount codes?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Discount codes may be available periodically. Check our coupon guide for current offers and how to apply them.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I check product quality?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Always request QC (Quality Control) photos before shipping. Our QC guide explains exactly what to look for.',
          },
        },
      ],
    },
  ],
};

export default function Home() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={homeJsonLd} />
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBgUrl} alt="Fashion boutique interior" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 via-primary-950/60 to-primary-950/40" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="max-w-3xl">
            <p className="text-sm md:text-base font-medium tracking-widest uppercase text-accent-300 mb-4">
              {t('hero_eyebrow')}
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-background-50 leading-tight mb-6">
              {t('hero_title')}
            </h1>
            <p className="text-base md:text-lg text-background-200 leading-relaxed mb-8 max-w-2xl">
              {t('hero_desc')}
            </p>

            {/* Search Box */}
            <div className="flex flex-col sm:flex-row gap-3 mb-8 max-w-xl">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-background-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('hero_search_placeholder')}
                  className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-background-50/95 backdrop-blur text-foreground-800 placeholder:text-foreground-400 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400/50"
                />
              </div>
              <button className="px-6 py-3.5 bg-accent-500 text-background-50 rounded-lg font-medium text-sm hover:bg-accent-600 transition-colors whitespace-nowrap">
                {t('search')}
              </button>
            </div>

            <div className="flex flex-wrap gap-3 mb-6">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-accent-500 text-background-50 rounded-lg font-medium text-sm hover:bg-accent-600 transition-colors inline-flex items-center gap-2"
              >
                {t('hero_cta_primary')}
                <i className="ri-arrow-right-line" />
              </a>
              <a
                href="/allchinabuy-spreadsheet-guide"
                className="px-6 py-3 bg-background-50/20 backdrop-blur text-background-50 border border-background-200/50 rounded-lg font-medium text-sm hover:bg-background-50/30 transition-colors inline-flex items-center gap-2"
              >
                {t('hero_cta_secondary')}
              </a>
            </div>

            <p className="text-xs text-background-300 tracking-wide">
              {t('hero_trust')}
            </p>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-background-100 border-y border-background-200/70">
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              { icon: 'ri-table-line', title: 'trust_spreadsheet', desc: 'trust_spreadsheet_desc' },
              { icon: 'ri-book-open-line', title: 'trust_guides', desc: 'trust_guides_desc' },
              { icon: 'ri-shield-check-line', title: 'trust_qc', desc: 'trust_qc_desc' },
              { icon: 'ri-truck-line', title: 'trust_shipping', desc: 'trust_shipping_desc' },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-lg md:text-xl text-primary-600`} />
                </div>
                <div>
                  <h3 className="font-medium text-sm md:text-base text-foreground-800 mb-1">{t(item.title)}</h3>
                  <p className="text-xs md:text-sm text-foreground-500">{t(item.desc)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Is Section */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-6">
            {t('what_is_title')}
          </h2>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8 max-w-3xl mx-auto">
            {t('what_is_desc')}
          </p>
          <a
            href="/allchinabuy-spreadsheet-guide"
            className="inline-flex items-center gap-2 text-accent-600 font-medium hover:text-accent-700 transition-colors"
          >
            {t('what_is_cta')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </section>

      {/* Categories Section */}
      <section className="bg-background-100 w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-4">
            {t('categories_title')}
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-xl">
            Explore our curated categories and find the best products on AllChinaBuy through our spreadsheet resources.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 md:gap-5">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-accent-300/50 transition-all duration-300"
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={cat.imageUrl}
                  alt={t(cat.nameKey)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="px-2 py-3 text-center bg-background-50">
                <span className="text-foreground-800 text-xs md:text-sm font-medium block leading-snug">
                  {t(cat.nameKey)}
                </span>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 text-accent-600 font-medium hover:text-accent-700 transition-colors"
          >
            {t('categories_cta')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-4">
            Shop Products
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
            Browse curated products from kaspreadsheet.com. Click any item to view details and purchase directly on the original site.
          </p>
        </div>

        {productCategories.map((cat) => (
          <ProductCarousel
            key={cat.key}
            categoryName={cat.name}
            products={cat.products}
          />
        ))}

        <div className="mt-8 text-center">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-4 bg-accent-500 text-background-50 rounded-lg font-medium text-sm hover:bg-accent-600 transition-colors"
          >
            View All Products on kaspreadsheet.com
            <i className="ri-external-link-line" />
          </a>
        </div>
      </section>

      {/* Popular Searches */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-10">
          {t('popular_title')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {popularSearches.map((item) => (
            <a
              key={item.key}
              href={item.href}
              target={item.external ? '_blank' : undefined}
              rel={item.external ? 'noopener nofollow' : undefined}
              className="group p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-300/50 hover:bg-accent-50/30 transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4 group-hover:bg-accent-100 transition-colors">
                <i className="ri-search-line text-primary-600 group-hover:text-accent-600" />
              </div>
              <h3 className="font-medium text-sm md:text-base text-foreground-800 group-hover:text-accent-700 transition-colors">
                {t(item.key)}
              </h3>
            </a>
          ))}
        </div>
      </section>

      {/* Buying Guides */}
      <section className="bg-background-100 w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-4">
            {t('guides_title')}
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
            {t('guides_desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {guides.map((guide) => (
            <a
              key={guide.id}
              href="/allchinabuy-spreadsheet-guide"
              className="group p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-300/50 hover:shadow-lg transition-all duration-300"
            >
              <div className={`w-12 h-12 rounded-lg ${guide.color} flex items-center justify-center mb-5`}>
                <i className={`${guide.icon} text-xl`} />
              </div>
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-800 mb-2 group-hover:text-accent-700 transition-colors">
                {t(`guide_${guide.id}_title`)}
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                {t(`guide_${guide.id}_desc`)}
              </p>
            </a>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href="/allchinabuy-spreadsheet-guide"
            className="inline-flex items-center gap-2 text-accent-600 font-medium hover:text-accent-700 transition-colors"
          >
            {t('guides_cta')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </section>

      {/* Pillar Articles */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-4">
            {t('pillar_title')}
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
            {t('pillar_desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={`/articles/${a.slug}`}
              className="group p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-300/50 transition-all duration-300"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-medium text-accent-600 uppercase tracking-wider">{a.category}</span>
                <span className="text-xs text-foreground-400">· {a.readTime}</span>
              </div>
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-800 mb-2 group-hover:text-accent-700 transition-colors">
                {a.title}
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                {a.subtitle}
              </p>
              <span className="inline-flex items-center gap-1 text-sm text-accent-600 font-medium group-hover:gap-2 transition-all">
                {t('read_more')} <i className="ri-arrow-right-line" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Comparison Section */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-10">
          {t('comparison_title')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {comparisons.map((item) => (
            <a
              key={item.key}
              href={item.href}
              target="_blank"
              rel="noopener nofollow"
              className="group p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-300/50 hover:bg-accent-50/30 transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-secondary-50 flex items-center justify-center">
                  <i className="ri-arrow-left-right-line text-secondary-600 text-sm" />
                </div>
                <span className="text-xs font-medium text-secondary-600 uppercase tracking-wider">VS</span>
              </div>
              <h3 className="font-medium text-sm md:text-base text-foreground-800 group-hover:text-accent-700 transition-colors">
                {t(item.key)}
              </h3>
            </a>
          ))}
        </div>
        <div className="mt-8 text-center">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 text-accent-600 font-medium hover:text-accent-700 transition-colors"
          >
            {t('comparison_cta')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </section>

      {/* Safety Section */}
      <section className="bg-primary-950 w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-accent-500/20 flex items-center justify-center mx-auto mb-6">
            <i className="ri-shield-check-line text-3xl text-accent-400" />
          </div>
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-background-50 mb-6">
            {t('safety_title')}
          </h2>
          <p className="text-base md:text-lg text-background-300 leading-relaxed mb-8 max-w-2xl mx-auto">
            {t('safety_desc')}
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-lg font-medium text-sm hover:bg-accent-600 transition-colors"
          >
            {t('safety_cta')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </section>

      {/* Review Section */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="flex-1">
              <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-6">
                {t('review_title')}
              </h2>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
                {t('review_desc')}
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 bg-foreground-900 text-background-50 rounded-lg font-medium text-sm hover:bg-foreground-800 transition-colors inline-flex items-center gap-2"
                >
                  {t('review_cta')}
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
            <div className="w-full md:w-80 shrink-0">
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm" />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-foreground-700">4.8 / 5</span>
                </div>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  Based on community feedback and independent research, AllChinaBuy provides a reliable platform for international shoppers seeking quality products at competitive prices.
                </p>
                <div className="text-xs text-foreground-400">
                  Last updated: August 2026
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-background-100 w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-10 text-center">
          {t('faq_title')}
        </h2>
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
            >
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                className="w-full flex items-center justify-between p-5 md:p-6 text-left hover:bg-background-100/50 transition-colors"
              >
                <span className="font-medium text-sm md:text-base text-foreground-800 pr-4">
                  {t(faq.q)}
                </span>
                <i className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-lg text-foreground-400 shrink-0`} />
              </button>
              {openFaq === index && (
                <div className="px-5 md:px-6 pb-5 md:pb-6">
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    {t(faq.a)}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden w-full px-4 md:px-8 lg:px-12 py-20 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-950 to-primary-900" />
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20warm%20amber%20gold%20tones%20minimal%20texture%20subtle%20elegant%20background%20soft%20gradient%20no%20people%20luxury%20aesthetic&width=1600&height=900&seq=cta-bg-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-semibold text-background-50 mb-6">
            {t('final_cta_title')}
          </h2>
          <p className="text-base md:text-lg text-background-300 leading-relaxed mb-10 max-w-2xl mx-auto">
            {t('final_cta_desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-4 bg-accent-500 text-background-50 rounded-lg font-medium text-sm hover:bg-accent-600 transition-colors inline-flex items-center gap-2"
            >
              {t('final_cta_primary')}
              <i className="ri-arrow-right-line" />
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-4 bg-background-50/10 backdrop-blur text-background-50 border border-background-200/30 rounded-lg font-medium text-sm hover:bg-background-50/20 transition-colors inline-flex items-center gap-2"
            >
              {t('final_cta_secondary')}
              <i className="ri-external-link-line" />
            </a>
          </div>
          <div className="mt-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-4 bg-background-50 text-primary-900 rounded-lg font-semibold text-base hover:bg-background-100 transition-colors"
            >
              {t('final_cta_buy_now')}
              <i className="ri-shopping-bag-line" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}